import React from 'react';
import { Table, Tag, Input, Select, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import type { ModuleListItem, FilterState } from '../../../services/devApi';

const { Text } = Typography;

interface ModuleTableProps {
  modules: ModuleListItem[];
  total: number;
  loading: boolean;
  filters: FilterState;
  selectedFormId: string | null;
  onSearch: (value: string) => void;
  onStatusFilter: (status: string) => void;
  onPageChange: (page: number, size: number) => void;
  onSort: (sortBy?: string, order?: 'asc' | 'desc') => void;
  onRowClick: (formId: string) => void;
}

const STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'done', label: '已完成' },
  { value: 'wip', label: '進行中' },
  { value: 'bug', label: '有 Bug' },
  { value: 'blocked', label: '已阻塞' },
  { value: '--', label: '未開始' },
];

function getStatusTag(status: string): React.ReactNode {
  const map: Record<string, { text: string; color: string }> = {
    '--': { text: '--', color: 'default' },
    wip: { text: 'WIP', color: 'processing' },
    done: { text: 'DONE', color: 'success' },
    bug: { text: 'BUG', color: 'error' },
    blocked: { text: 'BLOCKED', color: 'warning' },
  };
  const cfg = map[status] || map['--'];
  return <Tag color={cfg.color}>{cfg.text}</Tag>;
}

const DEP_LEVEL_MAP: Record<number, { text: string; color: string }> = {
  0: { text: 'P0 設定', color: '#52c41a' },
  1: { text: 'P1 主檔', color: '#1890ff' },
  2: { text: 'P2 交易', color: '#faad14' },
  3: { text: 'P3 財務', color: '#fa541c' },
  4: { text: 'P4 輔助', color: '#8c8c8c' },
};

function renderDepLevel(level: number | null): React.ReactNode {
  if (level === null || level === undefined) return <span className="text-gray-400">--</span>;
  const cfg = DEP_LEVEL_MAP[level] || { text: `P${level}`, color: '#8c8c8c' };
  return <Tag color={cfg.color}>{cfg.text}</Tag>;
}

function renderSolidScore(score: number): React.ReactNode {
  if (score === 0) {
    return <span className="text-gray-400">--/90</span>;
  }
  const pass = score >= 72;
  return (
    <span className={`font-bold ${pass ? 'text-green-600' : 'text-red-600'}`}>
      {score}/90
    </span>
  );
}

const ModuleTable: React.FC<ModuleTableProps> = ({
  modules,
  total,
  loading,
  filters,
  selectedFormId,
  onSearch,
  onStatusFilter,
  onPageChange,
  onSort,
  onRowClick,
}) => {
  const columns: ColumnsType<ModuleListItem> = [
    {
      title: '模組名稱',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name: string, record) => name || record.formId,
    },
    {
      title: 'FormId',
      dataIndex: 'formId',
      key: 'formId',
      width: 120,
      render: (formId: string) => <span className="font-mono">{formId}</span>,
    },
    {
      title: '依賴度',
      dataIndex: 'dependencyLevel',
      key: 'dependencyLevel',
      width: 90,
      sorter: true,
      sortOrder: filters.sortBy === 'dependencyLevel' ? (filters.order === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (level: number | null) => renderDepLevel(level),
    },
    {
      title: 'KB',
      dataIndex: 'specSizeKb',
      key: 'specSizeKb',
      width: 70,
      sorter: true,
      sortOrder: filters.sortBy === 'specSizeKb' ? (filters.order === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (kb: number) =>
        kb > 0 ? <span className="font-mono">{kb}</span> : <span className="text-gray-400">--</span>,
    },
    {
      title: '後端',
      dataIndex: 'backendStatus',
      key: 'backendStatus',
      width: 80,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'BE-SOLID',
      dataIndex: 'beSolidScore',
      key: 'beSolidScore',
      width: 90,
      sorter: true,
      sortOrder: filters.sortBy === 'beSolidScore' ? (filters.order === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (score: number) => renderSolidScore(score),
    },
    {
      title: '前端',
      dataIndex: 'frontendStatus',
      key: 'frontendStatus',
      width: 80,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'FE-SOLID',
      dataIndex: 'feSolidScore',
      key: 'feSolidScore',
      width: 90,
      sorter: true,
      sortOrder: filters.sortBy === 'feSolidScore' ? (filters.order === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (score: number) => renderSolidScore(score),
    },
    {
      title: 'E2E',
      dataIndex: 'e2eStatus',
      key: 'e2eStatus',
      width: 80,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '總狀態',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '子模組',
      dataIndex: 'submoduleCount',
      key: 'submoduleCount',
      width: 70,
      render: (count: number) => (count > 0 ? count : null),
    },
  ];

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <Input.Search
          placeholder="搜尋 formId..."
          allowClear
          style={{ width: 250 }}
          defaultValue={filters.search}
          onSearch={onSearch}
        />
        <Select
          placeholder="篩選狀態"
          allowClear
          style={{ width: 150 }}
          value={filters.status || undefined}
          onChange={(val) => onStatusFilter(val || '')}
          options={STATUS_OPTIONS}
        />
        <Text type="secondary">共 {total} 個模組</Text>
      </div>
      <Table<ModuleListItem>
        columns={columns}
        dataSource={modules}
        rowKey="formId"
        loading={loading}
        scroll={{ x: 900 }}
        pagination={{
          current: filters.page,
          pageSize: filters.size,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
        }}
        onChange={(pagination: TablePaginationConfig, _filters, sorter) => {
          const s = sorter as SorterResult<ModuleListItem>;
          const newPage = pagination.current || 1;
          const newSize = pagination.pageSize || 50;
          // Check if sort changed
          const prevSortBy = filters.sortBy;
          const newSortBy = s.order ? (s.field as string) : undefined;
          const newOrder = s.order === 'ascend' ? 'asc' : s.order === 'descend' ? 'desc' : undefined;
          if (newSortBy !== prevSortBy || newOrder !== filters.order) {
            onSort(newSortBy, newOrder);
          } else {
            onPageChange(newPage, newSize);
          }
        }}
        onRow={(record) => ({
          onClick: () => onRowClick(record.formId),
          style: { cursor: 'pointer' },
        })}
        rowClassName={(record) =>
          record.formId === selectedFormId ? 'bg-blue-50' : ''
        }
        size="middle"
      />
    </div>
  );
};

export default ModuleTable;
